import axios from 'axios';
import clsx from 'clsx';
import { useEffect, useId, useState } from 'react';
import toast from 'react-hot-toast';
import {
  DONATION_TONE,
  IC_CONSENT_TEXT_VERSION,
  IC_SAVE_MESSAGES
} from '../../constants/donation.const';
import { apiErrorCode } from '../../api/donation.api';
import { useApp } from '../../context/usePortal';
import {
  useDonorTaxProfile,
  useSaveIcNumber
} from '../../hooks/donations/useDonations';
import { DONATION_ERROR_CODES } from '../../types/donation.types';
import {
  formatIcNumber,
  icNumberDigits,
  isCompleteIcNumber,
  maskedIcNumber
} from '../../utils/donation-format';
import { FIELD_LABEL } from '../detail/detail-classes';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle
} from '../ui/drawer';

/** Opened by a receipt download, or from My Donations to add the number on its own. */
export type AddIcNumberPurpose = 'receipt' | 'profile';

interface AddIcNumberSheetProps {
  open: boolean;
  purpose: AddIcNumberPurpose;
  onOpenChange: (open: boolean) => void;
  /** Called once the number is saved, before the sheet closes. */
  onSaved?: () => void;
}

/** One toast slot for a save's outcome, so a success replaces an earlier error. */
const IC_SAVE_TOAST_ID = 'ic-number-save';

const SUBMIT_LABEL: Record<AddIcNumberPurpose, string> = {
  receipt: 'Save and download receipt',
  profile: 'Save IC number'
};

const saveErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) return IC_SAVE_MESSAGES.failed;
  switch (apiErrorCode(error)) {
    case DONATION_ERROR_CODES.DONOR_ID_EXISTS:
      return IC_SAVE_MESSAGES.exists;
    case DONATION_ERROR_CODES.CONSENT_TEXT_OUTDATED:
      return IC_SAVE_MESSAGES.outdated;
    case DONATION_ERROR_CODES.DONOR_ID_NOT_APPLICABLE:
      return IC_SAVE_MESSAGES.notApplicable;
  }
  if (error.response?.status === 400) return IC_SAVE_MESSAGES.invalid;
  return IC_SAVE_MESSAGES.failed;
};

/** The donor's account needs no ID on its receipts, so the sheet has nothing left to ask. */
const isNotApplicable = (error: unknown) =>
  apiErrorCode(error) === DONATION_ERROR_CODES.DONOR_ID_NOT_APPLICABLE;

export function AddIcNumberSheet({
  open,
  purpose,
  onOpenChange,
  onSaved
}: AddIcNumberSheetProps) {
  const { user } = useApp();
  const { data: taxProfile } = useDonorTaxProfile(open);
  const idRule = taxProfile?.id_rule ?? null;
  const digits = idRule?.digits;
  const saveIcNumber = useSaveIcNumber();
  const [icNumber, setIcNumber] = useState('');
  const [consented, setConsented] = useState(false);
  const fieldId = useId();
  const hintId = useId();
  const consentId = useId();

  useEffect(() => {
    if (!open) {
      setIcNumber('');
      setConsented(false);
    }
  }, [open]);

  const canSubmit =
    isCompleteIcNumber(icNumber, digits) &&
    consented &&
    !saveIcNumber.isPending;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    saveIcNumber.mutate(
      {
        ic_number: icNumberDigits(icNumber, digits),
        consent: true,
        consent_text_version: IC_CONSENT_TEXT_VERSION
      },
      {
        onSuccess: (profile) => {
          toast.success(
            profile.ic_last4
              ? `IC number ${maskedIcNumber(profile.ic_last4)} saved.`
              : 'IC number saved.',
            { id: IC_SAVE_TOAST_ID }
          );
          onSaved?.();
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(saveErrorMessage(error), { id: IC_SAVE_TOAST_ID });
          if (isNotApplicable(error)) onOpenChange(false);
        }
      }
    );
  };

  // Only a nationality whose tax receipts need an ID is ever asked for one.
  if (!idRule) return null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto max-w-lg rounded-t-3xl">
        <form onSubmit={handleSubmit} className="px-5 pb-6 pt-4">
          <DrawerTitle className="text-xl font-semibold text-gray-900">
            Add your IC number
          </DrawerTitle>
          <DrawerDescription className="mt-2 text-sm text-gray-600">
            LHDN requires your name, IC number and email on tax-deductible
            receipts. You only need to do this once, and it applies to all your
            past donations.
          </DrawerDescription>

          <div className="mt-4 rounded-2xl bg-gray-50 p-4">
            <dl className="grid grid-cols-2 gap-4">
              <div className="min-w-0">
                <dt className={FIELD_LABEL}>Name</dt>
                <dd className="mt-1 break-words text-sm text-gray-900">
                  {user?.user_name || '—'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className={FIELD_LABEL}>Email</dt>
                <dd className="mt-1 break-all text-sm text-gray-900">
                  {user?.user_email || '—'}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-gray-500">
              From your profile. Change them in Settings.
            </p>
          </div>

          <label
            htmlFor={fieldId}
            className="mt-5 block text-sm font-medium text-gray-900"
          >
            {idRule.label}
          </label>
          <input
            id={fieldId}
            inputMode="numeric"
            autoComplete="off"
            value={icNumber}
            onChange={(e) =>
              setIcNumber(formatIcNumber(e.target.value, digits))
            }
            placeholder="e.g. 900101-14-5678"
            aria-describedby={hintId}
            className="mt-2 block w-full rounded-xl border border-gray-300 px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:border-[#5CB891] focus:outline-none focus:ring-1 focus:ring-[#5CB891]"
          />
          <p id={hintId} className="mt-2 text-xs text-gray-500">
            {idRule.digits} digits. We add the dashes for you.
          </p>

          <div className="mt-5 flex items-start gap-3">
            <Checkbox
              id={consentId}
              checked={consented}
              onCheckedChange={(checked) => setConsented(checked === true)}
              className="mt-0.5 h-5 w-5 rounded border-gray-400 data-[state=checked]:border-emerald-700 data-[state=checked]:bg-emerald-700"
            />
            <label htmlFor={consentId} className="text-xs text-gray-700">
              I agree that my name, IC number and email are printed on my
              donation receipts and shared with the charity for tax purposes.
            </label>
          </div>

          <Button
            type="submit"
            disabled={!canSubmit}
            className={clsx(
              'mt-6 h-12 w-full rounded-xl text-sm font-semibold',
              DONATION_TONE.filledButton,
              'disabled:bg-gray-200 disabled:text-gray-500 disabled:opacity-100'
            )}
          >
            {saveIcNumber.isPending ? 'Saving...' : SUBMIT_LABEL[purpose]}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="mt-2 h-11 w-full text-sm font-medium text-gray-700"
          >
            Not now
          </Button>
        </form>
      </DrawerContent>
    </Drawer>
  );
}
